import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateUtils } from '@vectord/utils';
import pluralize from 'pluralize';

import {
  AdjacentCoursePauseRequestButton,
  ADJACENT_COURSE_PAUSE_REQUEST_TYPE,
  CoursePauseBatchLecturesList,
  CoursePauseLearnerLecturesList,
  COURSE_PAUSE_REQUEST_STATUSES,
  ManageModalBadge,
  ManageStageSuperBatchSelect,
  selectedRequestSelector,
  selectedSbatLectureSelector,
  selectedSuperBatchSelector,
  setSelectedSbatLecture,
  setSelectedSuperBatch,
} from 'features/courseResume';
import AttentionMark from 'components/AttentionMark';
import RescheduleRequestButton from '../RescheduleResumeButton';
import styles from './CoursePauseRescheduleModalManageStage.module.scss';
import toDDMMMYYYYHHMM from 'utils/toDDMMMYYYYHHMM';
import UnscheduleRequestButton from '../UnscheduleRequestButton';

function CoursePauseRescheduleModalManageStage() {
  const dispatch = useDispatch();
  const selectedSuperBatch = useSelector(selectedSuperBatchSelector);
  const selectedRequest = useSelector(selectedRequestSelector);
  const selectedSbatLecture = useSelector(selectedSbatLectureSelector);
  const { date_of_topic: dateOfTopic } = selectedSbatLecture || {};
  const {
    duration_of_pause: durationOfPause,
    pause_date: pauseDate,
    expected_resume_date: expectedResumeDate,
    source_batch: sourceBatch,
    destination_batch: destinationBatch,
    scheduled_resume_date: scheduledResumeDate,
    mentee,
  } = selectedRequest;
  const { super_batch: sourceSuperBatch } = sourceBatch;
  const { super_batch: destinationSuperBatch } = destinationBatch;
  const resumeDate =
    (dateOfTopic && toDDMMMYYYYHHMM(dateOfTopic)) ||
    toDDMMMYYYYHHMM(scheduledResumeDate);
  const handleLectureSelect = useCallback(
    (lecture) => {
      dispatch(setSelectedSbatLecture(lecture));
    },
    [dispatch]
  );

  useEffect(() => {
    if (destinationSuperBatch) {
      dispatch(
        setSelectedSuperBatch({
          id: destinationSuperBatch.id,
          name: destinationSuperBatch.name,
        })
      );
    }
  }, [destinationSuperBatch, dispatch]);

  return (
    <div className={styles.root}>
      <div className="h4 primary bold m-b-xs">MANAGE RESUME</div>
      <ManageStageSuperBatchSelect selectedSuperBatch={selectedSuperBatch} />
      <>
        <div className={styles.listSection}>
          <div className={styles.lectureList}>
            <div className="h6">CURRENT</div>
            <CoursePauseLearnerLecturesList
              superBatch={sourceSuperBatch}
              request={selectedRequest}
              referenceDate={pauseDate}
            />
          </div>
          <div className={styles.lectureList}>
            {selectedSuperBatch && (
              <>
                <div className="h6">NEW</div>
                <CoursePauseBatchLecturesList
                  superBatch={selectedSuperBatch}
                  coursePauseRequest={selectedRequest}
                  referenceDate={Math.max(
                    new Date(scheduledResumeDate),
                    new Date()
                  )}
                  isSelectable
                  onSelect={handleLectureSelect}
                  selectedLecture={selectedSbatLecture}
                />
              </>
            )}
          </div>
        </div>
        <div className={styles.dateSection}>
          <ManageModalBadge
            heading="Learner Email"
            data={mentee.user.email}
          />
          <ManageModalBadge
            heading="Paused From"
            data={DateUtils.toDDMMMYYYY(pauseDate)}
          />
          <ManageModalBadge
            heading="Pause Duration"
            data={pluralize('Month', durationOfPause, true)}
          />
          <ManageModalBadge
            heading="Expected Resume Date"
            data={DateUtils.toDDMMMYYYY(expectedResumeDate)}
          />
          <ManageModalBadge
            heading="Scheduled Resume Date"
            data={resumeDate}
          />
        </div>
        <div className="row">
          <RescheduleRequestButton />
          <UnscheduleRequestButton />
        </div>
        {selectedRequest.status ===
          COURSE_PAUSE_REQUEST_STATUSES.scheduledAccept && (
          <div className="danger m-t-xxs h5">
            <AttentionMark className="m-r-xxs" />
            The Course pause request is currently in the "scheduled accept"
            status. Rescheduling is disabled. Please unschedule and then attempt
            the action again.
          </div>
        )}
      </>
      <div className="row justify-c align-c m-t-s">
        <AdjacentCoursePauseRequestButton
          type={ADJACENT_COURSE_PAUSE_REQUEST_TYPE.previous}
        />
        <AdjacentCoursePauseRequestButton
          type={ADJACENT_COURSE_PAUSE_REQUEST_TYPE.next}
        />
      </div>
    </div>
  );
}

export default CoursePauseRescheduleModalManageStage;
